import React from "react";
import ReactApexChart from "react-apexcharts";

const ScaleChart = ({ questionData, responses, chartTitle = "Scale Responses" }) => {
  // Determine the max score (5 or 10)
  let maxScore = 5;
  if (questionData.type === "scale") {
    if (questionData.scale === "1-5" || questionData.scale === "0-5") {
      maxScore = 5;
    } else if (questionData.scale === "0-10") {
      maxScore = 10;
    }
  }

  // Initialize counts array, one slot for each possible score from 0 to maxScore
  const counts = Array(maxScore + 1).fill(0);

  // Populate counts array from responses
  // For example, if a response has "task_list[0].response = 3", increment counts[3]
  responses.forEach((r) => {
    const score = r?.task_list?.[0]?.response;
    if (typeof score === "number" && score >= 0 && score <= maxScore) {
      counts[score] += 1;
    }
  });

  // X-axis labels: "0", "1", "2", etc.
  const categories = counts.map((_, idx) => `${idx}`);

  // Series for ApexCharts: single series with the count data
  const series = [
    {
      name: "Responses",
      data: counts,
    },
  ];

  // Calculate the maximum count to help scale the y-axis nicely
  const maxCount = Math.max(...counts);

  // Chart styling (inspired by your snippet)
  const options = {
    chart: {
      height: 400,
      type: "bar",
      id: "my-chart",
      fontFamily: "Raleway, sans-serif",
      foreColor: "#7E7E7E",
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    colors: ["#2D70E2"], // Bar color
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 5,
        barHeight: "70%",
      },
    },
    legend: {
      show: false,
    },
    fill: {
      type: "solid",
    },
    grid: {
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
    },
    xaxis: {
      categories,
      title: {
        text: "Score", // X-axis title
      },
      labels: {
        style: {
          fontFamily: "Raleway, sans-serif",
        },
      },
    },
    yaxis: {
      min: 0,
      max: maxCount < 5 ? 5 : maxCount,
      tickAmount: 5,
      forceNiceScale: false,
      labels: {
        style: {
          fontFamily: "Raleway, sans-serif",
        },
        show: true,
        formatter: function (val, index) {
          // Create a custom set of labels to prevent duplicates
          const maxVal = maxCount < 5 ? 5 : maxCount;
          const step = maxVal / 5;
          const expectedValues = [0, step, step * 2, step * 3, step * 4, maxVal];
          const rounded = Math.round(val);

          // Only show labels at expected positions
          const closest = expectedValues.reduce((prev, curr) =>
            Math.abs(curr - val) < Math.abs(prev - val) ? curr : prev
          );

          return Math.abs(closest - val) < 0.1 ? Math.round(closest) : '';
        },
      },
      axisBorder: {
        show: true,
      },
      axisTicks: {
        show: true,
      },
      title: {
        text: "Number of Responses",
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        // Show raw counts (e.g., "5")
        return val;
      },
      style: {
        fontSize: "14px",
        fontFamily: "Raleway, sans-serif",
        colors: ["#FFFFFF"], // White text inside the bar
      },
      offsetY: 0,
      dropShadow: {
        enabled: true,
        top: 1,
        left: 1,
        blur: 1,
        color: "#000",
        opacity: 0.3,
      },
    },
    tooltip: {
      enabled: true,
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        // Provide additional context if you wish
        const scoreValue = w.globals.labels[dataPointIndex];
        const countValue = series[seriesIndex][dataPointIndex];
        return `
          <div style="padding: 8px; background-color: white; border: 1px solid #e0e0e0; border-radius: 5px; font-family: Raleway, sans-serif;">
            <div style="font-size: 14px; font-weight: 500; color: #333;">
              Score: ${scoreValue} <br />
              Responses: ${countValue}
            </div>
          </div>
        `;
      },
    },
  };

  return (
    <div style={{ width: "100%" }}>
      <ReactApexChart options={options} series={series} type="bar" height={400} />
    </div>
  );
};

const MultipleChoiceChart = ({ questionData, responses, chartTitle = "Multiple Choice Responses" }) => {
  // Get all possible options from the question
  const questionOptions = questionData.options || [];

  // Initialize counts for each option
  const counts = Array(questionOptions.length).fill(0);

  // Count responses for each option
  responses.forEach((r) => {
    const responseStr = r?.task_list?.[0]?.response;
    if (responseStr) {
      if (questionData.type === "multi") {
        try {
          // For multi type, parse the JSON string response into an array
          const responseArray = JSON.parse(responseStr);

          // For each response in the array, find its index in questionOptions and increment the count
          responseArray.forEach((response) => {
            const optionIndex = questionOptions.indexOf(response);
            if (optionIndex !== -1) {
              counts[optionIndex] += 1;
            }
          });
        } catch (e) {
          console.error('Error parsing response:', e);
        }
      } else if (questionData.type === "single") {
        // For single type, directly use the response string
        const optionIndex = questionOptions.indexOf(responseStr);
        if (optionIndex !== -1) {
          counts[optionIndex] += 1;
        }
      }
    }
  });

  // Calculate total responses and percentages
  const totalResponses = counts.reduce((sum, count) => sum + count, 0);
  const percentages = counts.map(count => totalResponses > 0 ? Math.round((count / totalResponses) * 100) : 0);

  if (questionData.type === "single") {
    // Pie chart for single choice
    const series = counts;
    const options = {
      chart: {
        type: 'pie',
        height: 400,
        fontFamily: "Raleway, sans-serif",
        foreColor: "#7E7E7E",
      },
      labels: questionOptions,
      colors: ['#2D70E2', '#4CAF50', '#FF9800', '#E91E63', '#9C27B0', '#00BCD4', '#FFC107', '#795548'],
      legend: {
        position: 'right',
        fontFamily: "Raleway, sans-serif",
      },
      dataLabels: {
        enabled: true,
        formatter: function (val) {
          return val === 0 ? '' : val?.toFixed(2) + '%';
        },
        style: {
          fontSize: '14px',
          fontFamily: "Raleway, sans-serif",
        }
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: function (val) {
            return val + ' responses';
          }
        }
      }
    };

    return (
      <div style={{ width: "100%" }}>
        <ReactApexChart options={options} series={series} type="pie" height={400} />
      </div>
    );
  } else {
    // Horizontal bar chart for multi choice
    const series = [{
      name: 'Responses',
      data: counts.map((count, index) => ({
        x: questionOptions[index],
        y: count,
        percentage: percentages[index]
      }))
    }];

    const options = {
      chart: {
        type: 'bar',
        height: 400,
        fontFamily: "Raleway, sans-serif",
        foreColor: "#7E7E7E",
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 5,
          barHeight: '70%',
          distributed: false,
        }
      },
      colors: ['#2D70E2'],
      dataLabels: {
        enabled: true,
        formatter: function (val, opts) {
          const percentage = series[0].data[opts.dataPointIndex].percentage;
          return val === 0 ? '' : `${val} (${percentage?.toFixed(2)}%)`;
        },
        style: {
          fontSize: '14px',
          fontFamily: "Raleway, sans-serif",
          colors: ['#FFFFFF']
        },
        offsetX: 0,
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 1,
          color: '#000',
          opacity: 0.3
        }
      },
      xaxis: {
        type: 'category',
        categories: questionOptions,
        labels: {
          style: {
            fontFamily: "Raleway, sans-serif",
          }
        }
      },
      yaxis: {
        labels: {
          style: {
            fontFamily: "Raleway, sans-serif",
          }
        }
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: function (val, opts) {
            const percentage = series[0].data[opts.dataPointIndex].percentage;
            return `${val} responses (${percentage?.toFixed(2)}%)`;
          }
        }
      }
    };

    return (
      <div style={{ width: "100%" }}>
        <ReactApexChart options={options} series={series} type="bar" height={400} />
      </div>
    );
  }
};

const SummaryChart = ({ questionData, responses, chartTitle }) => {
  if (questionData.type === "scale") {
    return <ScaleChart questionData={questionData} responses={responses} chartTitle={chartTitle} />;
  } else if (questionData.type === "single" || questionData.type === "multi") {
    return <MultipleChoiceChart questionData={questionData} responses={responses} chartTitle={questionData.type === "single" ? "Multiple Choice Responses" : "Multiple Select Responses"} />;
  }
  return null;
};

export default SummaryChart;
